import { useState, type DragEvent } from 'react';
import { ETAPE_INFO, membresPresents, type Etape, type Foyer, type Groupe, type Score } from '@dinertournant/core';
import { IconeMaison, IconeMemeFoyer, IconePoignee } from './icones.js';

interface Props {
  groupe: Groupe;
  foyers: Map<string, Foyer>;
  score?: Score | undefined;
  /** Foyers déplacés à la main (étiquette « déplacé »). */
  deplaces?: Set<string> | undefined;
  /** Autres hôtes de la même étape : alternative clavier « Déplacer vers… ». */
  autresHotes?: { id: string; nom: string }[] | undefined;
  onDeplacer?: ((foyerId: string, versHoteFoyerId: string) => void) | undefined;
  /** Glisser-déposer : un foyer (bloc) déposé sur cette carte rejoint cette table. */
  onDeposer?: ((foyerId: string, versHoteFoyerId: string) => void) | undefined;
  /** Masque l'adresse (page publique / impression partielle). */
  sansAdresse?: boolean | undefined;
  /** Plage conseillée (cible ± 1) ; 3–5 par défaut. */
  plage?: { min: number; max: number } | undefined;
}

/** Carte de groupe (écran 5) : une table chez un hôte, les foyers en blocs indivisibles, paires qui se recroisent surlignées. */
export function CarteGroupe({ groupe, foyers, score, deplaces, autresHotes, onDeplacer, onDeposer, sansAdresse = false, plage = { min: 3, max: 5 } }: Props) {
  const [survol, setSurvol] = useState(false);
  const hote = foyers.get(groupe.hoteFoyerId);
  const taille = groupe.foyerIds.reduce((s, id) => s + (foyers.get(id) ? membresPresents(foyers.get(id)!).length : 0), 0);
  const horsPlage = taille < plage.min || taille > plage.max;
  const capacite = hote?.capaciteMax;
  const depasse = capacite !== undefined && taille > capacite;

  const onDragStart = (e: DragEvent, foyerId: string) => {
    e.dataTransfer.setData('text/plain', `${groupe.etape}:${foyerId}`);
    e.dataTransfer.effectAllowed = 'move';
  };
  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setSurvol(false);
    const [etape, foyerId] = e.dataTransfer.getData('text/plain').split(':');
    if (!onDeposer || !foyerId || etape !== groupe.etape) return;
    if (groupe.foyerIds.includes(foyerId)) return;
    onDeposer(foyerId, groupe.hoteFoyerId);
  };

  return (
    <section
      className={`carte-groupe etape-${groupe.etape}${survol ? ' survol' : ''}${horsPlage || depasse ? ' hors-plage' : ''}`}
      aria-label={`Chez ${hote?.nom ?? '?'}, ${taille} personnes`}
      onDragOver={onDeposer ? (e) => { e.preventDefault(); if (!survol) setSurvol(true); } : undefined}
      onDragLeave={onDeposer ? () => setSurvol(false) : undefined}
      onDrop={onDeposer ? onDrop : undefined}
    >
      <header className="entete">
        <div>
          <div className="titre">Chez {hote?.nom ?? '?'}</div>
          {hote?.adresse && !sansAdresse ? <div className="adresse">{hote.adresse}</div> : null}
        </div>
        <span className="taille" title={capacite ? `Capacité annoncée : ${capacite}` : undefined}>
          {capacite ? `${taille} / ${capacite}` : taille} pers.
          {depasse ? <span className="sr-only"> (capacité dépassée)</span> : null}
        </span>
      </header>
      <div className="corps">
        {groupe.foyerIds.map((id) => {
          const f = foyers.get(id);
          if (!f) return null;
          const estHote = id === groupe.hoteFoyerId;
          const presents = membresPresents(f);
          const couple = presents.length >= 2; // même foyer (2 ou 3 personnes) : bloc indivisible
          const recroisements = score?.foyersEnDouble.filter((d) => (d.foyerA === id || d.foyerB === id) && groupe.foyerIds.includes(d.foyerA === id ? d.foyerB : d.foyerA)) ?? [];
          const recroise = recroisements.length > 0;
          const mobile = !estHote && !!onDeposer;
          return (
            <div
              key={id}
              className={`bloc-foyer${recroise ? ' recroise' : couple ? ' couple' : ''}${mobile ? ' mobile' : ''}`}
              draggable={mobile}
              onDragStart={mobile ? (e) => onDragStart(e, id) : undefined}
            >
              <span className="poignee" style={estHote ? { visibility: 'hidden' } : undefined} aria-hidden="true">
                <IconePoignee />
              </span>
              <div className="personnes">
                {presents.map((p, i) => (
                  <div key={p.id} className={`personne${estHote ? ' hote-nom' : ''}`}>
                    <span>{p.prenomNom}</span>
                    {p.regime ? <span className="tag-regime">{p.regime}</span> : null}
                    {i === 0 && estHote ? (
                      <span className="badge-hote">
                        <IconeMaison taille={12} /> hôte
                      </span>
                    ) : null}
                    {i === 0 && couple ? (
                      <span className="meme-foyer" title="Même voiture : se déplacent ensemble" aria-label="même voiture">
                        <IconeMemeFoyer />
                      </span>
                    ) : null}
                    {i === 0 && deplaces?.has(id) ? <span className="deplace">déplacé</span> : null}
                  </div>
                ))}
                {recroisements.map((d) => {
                  const autre = foyers.get(d.foyerA === id ? d.foyerB : d.foyerA);
                  const ailleurs = d.lieux.filter((l) => l.etape !== groupe.etape).map((l) => ETAPE_INFO[l.etape as Etape].au);
                  return (
                    <div key={`${d.foyerA}-${d.foyerB}`} className="revoit">
                      Revoit {autre?.nom} {ailleurs.join(' et ')}
                    </div>
                  );
                })}
                {!estHote && onDeplacer && autresHotes && autresHotes.length > 0 ? (
                  <div>
                    <label className="sr-only" htmlFor={`deplacer-${groupe.etape}-${id}`}>
                      Déplacer {f.nom} vers
                    </label>
                    <select id={`deplacer-${groupe.etape}-${id}`} className="menu-deplacer" value="" onChange={(ev) => ev.target.value && onDeplacer(id, ev.target.value)}>
                      <option value="">Déplacer vers…</option>
                      {autresHotes
                        .filter((h) => h.id !== groupe.hoteFoyerId)
                        .map((h) => (
                          <option key={h.id} value={h.id}>
                            Chez {h.nom}
                          </option>
                        ))}
                    </select>
                  </div>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
